import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const NOTIFICATION_TYPES = [
  { id: 'capital-call', label: 'Capital Calls', icon: 'PoundSterling', description: 'Capital call notices, reminders, and confirmations' },
  { id: 'milestone', label: 'Milestones', icon: 'Flag', description: 'Stage gate updates, construction progress, and COD alerts' },
  { id: 'esg', label: 'ESG Alerts', icon: 'Leaf', description: 'ESG threshold breaches, report publications, and governance alerts' },
  { id: 'document', label: 'Documents', icon: 'FileText', description: 'New uploads, expiring documents, and signature requests' },
  { id: 'deal-movement', label: 'Deal Movements', icon: 'ArrowRightLeft', description: 'Stage progressions, new deals, and status updates' },
];

const PRIORITY_OPTIONS = ['All', 'Medium and above', 'High only'];

export default function SettingsModal({ onClose, onSave }) {
  const [settings, setSettings] = useState(
    Object.fromEntries(NOTIFICATION_TYPES?.map((t) => [
      t?.id,
      { enabled: true, priority: 'All', email: true, sms: false },
    ]))
  );

  const toggleEnabled = (id) => {
    setSettings((prev) => ({ ...prev, [id]: { ...prev?.[id], enabled: !prev?.[id]?.enabled } }));
  };

  const setPriority = (id, value) => {
    setSettings((prev) => ({ ...prev, [id]: { ...prev?.[id], priority: value } }));
  };

  const toggleEmail = (id) => {
    setSettings((prev) => ({ ...prev, [id]: { ...prev?.[id], email: !prev?.[id]?.email } }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="font-heading text-lg font-semibold text-foreground">Notification Preferences</h2>
            <p className="font-caption text-sm text-muted-foreground mt-0.5">Manage how and when you receive notifications</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Delivery methods header */}
          <div className="grid grid-cols-[1fr_120px_80px_80px_80px] gap-3 items-center pb-2 border-b border-border">
            <span className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">Notification Type</span>
            <span className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide text-center">Min Priority</span>
            <span className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide text-center">In-app</span>
            <span className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide text-center">Email</span>
            <span className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide text-center">SMS</span>
          </div>

          {NOTIFICATION_TYPES?.map((type) => {
            const s = settings?.[type?.id];
            return (
              <div key={type?.id} className="grid grid-cols-[1fr_120px_80px_80px_80px] gap-3 items-center py-3 border-b border-border/50">
                {/* Label */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleEnabled(type?.id)}
                    className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                      s?.enabled ? 'bg-primary' : 'bg-muted'
                    }`}
                    role="switch"
                    aria-checked={s?.enabled}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                        s?.enabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <Icon name={type?.icon} size={14} color="var(--color-muted-foreground)" />
                      <span className={`font-caption text-sm font-medium ${s?.enabled ? 'text-foreground' : 'text-muted-foreground'}`}>{type?.label}</span>
                    </div>
                    <p className="font-caption text-xs text-muted-foreground mt-0.5">{type?.description}</p>
                  </div>
                </div>
                {/* Priority */}
                <select
                  value={s?.priority}
                  onChange={(e) => setPriority(type?.id, e?.target?.value)}
                  disabled={!s?.enabled}
                  className="text-xs border border-border rounded-lg px-2 py-1.5 text-foreground bg-card focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {PRIORITY_OPTIONS?.map((opt) => <option key={opt}>{opt}</option>)}
                </select>
                {/* In-app (always on) */}
                <div className="flex justify-center">
                  <div className="w-8 h-4 rounded-full bg-primary relative">
                    <span className="absolute top-0.5 right-0.5 w-3 h-3 bg-white rounded-full shadow" />
                  </div>
                </div>
                {/* Email */}
                <div className="flex justify-center">
                  <button
                    onClick={() => toggleEmail(type?.id)}
                    disabled={!s?.enabled}
                    className={`relative w-8 h-4 rounded-full transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${
                      s?.email && s?.enabled ? 'bg-primary' : 'bg-muted'
                    }`}
                    role="switch"
                    aria-checked={s?.email && s?.enabled}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform duration-200 ${
                        s?.email && s?.enabled ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                {/* SMS (coming soon) */}
                <div className="flex flex-col items-center gap-0.5">
                  <div className="w-8 h-4 rounded-full bg-muted relative opacity-50 cursor-not-allowed">
                    <span className="absolute top-0.5 left-0.5 w-3 h-3 bg-muted-foreground/30 rounded-full shadow" />
                  </div>
                  <span className="font-caption text-[9px] text-muted-foreground whitespace-nowrap">Soon</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
          >
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
}
